
import {
    ApplicationUserModel,
    CategoryModel,
    CategoryUserModel,
    TenantUserLocationModel,
    LocationModel,
    RoleModel,
    TenantModel,
    TenantUserModel,
    UserRoleModel,
    AggregationEnum,
    QueryOperator,
    JunctionOperator,
    PlanItemStateEnum,
    ImplementationStateEnum,
} from '@/libs/Api';
import Page from '@/Page.vue';
import { Guid } from '@/libs/common/functions/guid';
import { Component, Prop, Ref, Watch } from 'vue-property-decorator';
import { tenantsStore, tenantUsersStore } from '@/libs/tenants/+state/store';
import { CrudAction, CrudReponse } from '@/libs/core/+state/models/crud-action';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { rolesStore } from '@/libs/roles/+state/store';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import { applicationUserStore } from '@/libs/user-settings/+state/store';
import { authStore } from '@/libs/auth/+store/store';
import { AuthAction, AuthGetter } from '@/libs/auth/models/auth-state';
import { cloneDeep } from 'lodash';
import ApiService from '@/libs/core/api-service';
import moment from 'moment';
import { locationsStore } from '@/libs/locations/+state/store';

@Component({})
export default class AccountDialog extends Page {
    dialog = false;
    valid = true;
    showToastExternalLocationError = false;
    id: string | null = null;
    roleId: string | null = null;
    categoryId: string | null = null;
    locationId: string | null = null;
    item: TenantUserModel | null = null;
    activeUserCountReached = false;
    originalIsActive = false;
    allowedCategories: CategoryUserModel[] = [];
    allowedLocations: TenantUserLocationModel[] = [];
    useQuickLogin = false;
    setNewPin = false;
    setNewPassword = false;
    newPin = '';
    newPassword = '';
    maxUserCountReached = false;
    agendaWarningDialog = false;
    agendaDeactivationPending = false;
    activeAgendaCounts: Record<string, number> = {};
    @Prop() categories!: CategoryModel[];
    @Ref() form!: any;
    pinRules = [
        (v: any) => !!v || this.$t('validation.thisFieldIsRequired'),
        (v: string) => (v && v.length <= 4) || this.$t('validation.max4numbers'),
        (v: string) => (v && /^[0-9]+$/gi.test(v)) || this.$t('validation.onlyNumbers'),
    ];
    protected get employeeIdRules() {
        const tenant = tenantsStore.useGetter(CrudGetter.Detail) as TenantModel;
        return [
            (v: string) =>
                !v || (v && v.toString().length <= tenant.quickLoginPersonalNumberPositionCount!) ||
                this.$t('validation.maxNumbers', { value: tenant.quickLoginPersonalNumberPositionCount! })
        ];
    }

    get passwordRules() {
        return [(v: any) => !!v || this.$i18n.t('validation.thisFieldIsRequired')];
    }

    get isNewItem(): boolean {
        return this.item?.id == Guid.EmptyGuid();
    }

    get saving(): boolean {
        return tenantUsersStore.useGetter(CrudGetter.Saving, this.id);
    }

    get activeRoles(): string[] {
        return this.item?.userRoles?.map((x) => x.roleId!) ?? [];
    }

    get activeCategories(): string[] {
        return this.allowedCategories?.map((x) => x.categoryId!) ?? [];
    }

    get isSingleTenant(): boolean {
        return authStore.useGetter(AuthGetter.GetIsSingleTenant) ?? false;
    }

    get useTpm(): boolean {
        return (window as any).UseTpm;
    }

    @Watch('dialog')
    dialogChanged(v: boolean) {
        if (!v) this.close();
    }

    @Watch('useQuickLogin')
    private useQuickLoginChanged(value: boolean) {
        if (!value) {
            this.setNewPin = false;
        }
    }

    open(id: string | null = null) {
        this.dialog = true;
        this.id = id;
        this.roleId = null;
        this.setNewPassword = false;
        this.setNewPin = false;
        this.newPin = '';
        this.newPassword = '';
        tenantUsersStore.dispatch(CrudAction.Get, { id: id, key: id });
        this.subscribe(tenantUsersStore, CrudReponse.Get, id)
            .then((e: TenantUserModel) => {
                this.item = e;
                this.originalIsActive = e.isActive!;
                this.allowedCategories = cloneDeep(e.categories!);
                this.allowedLocations = cloneDeep(e.locations ?? []);
                this.useQuickLogin = e.hasQuickLogin ?? false;
                if (!e.isActive) {
                    const tenant = tenantsStore.useGetter(CrudGetter.Detail) as TenantModel;
                    tenantUsersStore.dispatch(
                        CrudAction.GetAll,
                        createCrudQueryPayload<TenantUserModel>(
                            undefined,
                            [{ field: 'isActive', op: 'eq', comparand: true }],
                            undefined,
                            false,
                            'ActiveTenantUsers'
                        )
                    );
                    this.subscribe(tenantUsersStore, CrudReponse.GetAll, 'ActiveTenantUsers').then((e: TenantUserModel[]) => {
                        if (e.length >= (tenant.licenceUserCount ?? 0)) {
                            this.activeUserCountReached = true;
                        } else {
                            this.activeUserCountReached = false;
                        }
                    });
                } else {
                    this.activeUserCountReached = false;
                }
            })
            .catch((e) => {
                this.close();
                throw e;
            });
    }

    close() {
        this.dialog = false;
    }

    async save() {
        // Kontrola deaktivace uživatele
        if (
            this.item &&
            this.originalIsActive === true &&
            this.item.isActive === false &&
            !this.agendaDeactivationPending
        ) {
            // Zjisti aktivní agendy
            const agendaCounts = await this.getActiveAgendaCounts(this.item.id!);
            const hasActive = Object.values(agendaCounts).some((c) => c > 0);
            if (hasActive) {
                this.activeAgendaCounts = agendaCounts;
                this.agendaWarningDialog = true;
                return; // Přeruší uložení, čeká na uživatele
            }
        }
        // Validace: Externí uživatel musí mít alespoň 1 lokaci
        if (this.item?.isExternal && (!this.allowedLocations || this.allowedLocations.length === 0)) {
            this.showToastExternalLocationError = true;
            return;
        }
        if (!this.item?.accessAllCategories) {
            this.item!.categories = this.allowedCategories;
        } else {
            this.item!.categories = [];
        }
        this.item!.locations = this.allowedLocations;
        if (this.useQuickLogin && this.newPin?.length > 0) {
            await ApiService.api.setQuickLoginPin(this.item!.id!, this.newPin);
            this.item!.hasQuickLogin = true;
        } else {
            this.item!.hasQuickLogin = true;
        }
        this.item!.employeeId = this.item!.employeeId?.toString();
        if (this.isSingleTenant && this.isUserName(this.item!) && this.setNewPassword && this.newPassword?.length > 0) {
            this.item!.pwRequest = this.newPassword;
        }
        tenantUsersStore.dispatch(CrudAction.Update, { item: this.item, key: this.id });
        this.subscribe(tenantUsersStore, CrudReponse.Update, this.id).then((e) => {
            this.useQuickLogin = e.hasQuickLogin;
            tenantUsersStore.dispatch(CrudAction.GetAll, createCrudQueryPayload<TenantUserModel>());
            const user = applicationUserStore.useGetter(CrudGetter.Detail) as ApplicationUserModel;
            tenantUsersStore.dispatch(
                CrudAction.GetAll,
                createCrudQueryPayload<TenantUserModel>(
                    undefined,
                    [
                        {
                            field: 'userId',
                            op: 'eq',
                            comparand: user.id!,
                        },
                    ],
                    undefined,
                    false,
                    'me'
                )
            );
            this.subscribe(tenantUsersStore, CrudReponse.GetAll, 'me')
                .then((e: TenantUserModel[]) => {
                    authStore.dispatch(AuthAction.SetTenantUser, e[0]);
                    this.close();
                    this.$emit('save');
                })
                .catch((e: any) => {
                    console.error(e);
                });
        });
    }

    deleteClick() {
        console.log('deleteClick');
    }

    addRoleClick() {
        const roles = rolesStore.useGetter(CrudGetter.Data) as RoleModel[];
        const role = roles.firstOrDefault((x) => x.id == this.roleId);
        if (role) {
            this.item!.userRoles!.push({
                id: Guid.EmptyGuid(),
                tenantUserId: this.item!.id,
                roleId: role.id,
                role: role,
            } as UserRoleModel);
        }
        this.roleId = null;
    }

    addAllowedCategoryClick() {
        const category = this.categories.firstOrDefault((x) => x.id == this.categoryId);
        if (category) {
            this.allowedCategories.push({
                id: Guid.EmptyGuid(),
                categoryId: this.categoryId,
                category: category,
            } as CategoryUserModel);
        }
        this.categoryId = null;
    }

    deleteCategoryClick(index: number, category: CategoryModel) {
        this.allowedCategories.splice(index, 1);
    }

    addLocationClick() {
        if (!this.locationId) return;
        const exists = (this.allowedLocations ?? []).some((x) => x.locationId == this.locationId);
        if (!exists) {
            this.allowedLocations.push({
                id: Guid.EmptyGuid(),
                locationId: this.locationId,
            } as TenantUserLocationModel);
        }
        this.locationId = null;
    }

    deleteLocationClick(index: number) {
        this.allowedLocations.splice(index, 1);
    }

    getLocationName(locationId?: string | null): string {
        if (!locationId) return '';
        const all: LocationModel[] = locationsStore.useGetter(CrudGetter.Data) ?? [];
        const found: LocationModel | undefined = (all as any).firstOrDefault
            ? (all as any).firstOrDefault((x: LocationModel) => x.id == locationId)
            : all.find((x) => x.id == locationId);
        return found?.name ?? '';
    }

    protected isUserName(item: TenantUserModel): boolean {
        return /^[a-zA-Z0-9.]{6,}$/.test(item.user?.email ?? '');
    }

    async getActiveAgendaCounts(tenantUserId: string): Promise<Record<string, number>> {
        const result: Record<string, number> = {
            Plan: 0,
            Findings: 0,
            Measures: 0,
            Verification: 0,
            Review: 0,
        };
        const todayIso = moment().startOf('day').toISOString();
        // Plán
        try {
            const planRes = await ApiService.api.aggregationPlanItem({
                predicates: [
                    {
                        field: 'id',
                        op: QueryOperator.Wrap,
                        comparand: [
                            { field: 'tenantUserId', op: QueryOperator.Eq, comparand: tenantUserId, junction: JunctionOperator.Or },
                            { field: 'planItemAuditTenantUsers.tenantUserId', op: QueryOperator.Eq, comparand: tenantUserId, junction: JunctionOperator.Or },
                        ],
                    },
                    { field: 'time', op: QueryOperator.Gte, comparand: todayIso },
                    { field: 'isAnswered', op: QueryOperator.Eq, comparand: false },
                ],
                aggregation: AggregationEnum.Count,
                aggregationProperty: 'id',
            });
            let val = planRes?.data?.returnValue;
            if (val && typeof val === 'object' && 'result' in val) val = val.result;
            result.Plan = typeof val === 'number' ? val : (typeof val === 'string' ? parseInt(val, 10) : 0);
        } catch (e) { console.error(e); }
        // Nálezy
        try {
            const findingsRes = await ApiService.api.aggregationFinding({
                predicates: [
                    { field: 'findingManagers.tenantUserId', op: QueryOperator.Eq, comparand: tenantUserId },
                    { field: 'response.planItem.planItemState', op: QueryOperator.Eq, comparand: PlanItemStateEnum.NeedsToCreateMeasures },
                ],
                aggregation: AggregationEnum.Count,
                aggregationProperty: 'id',
            });
            let val = findingsRes?.data?.returnValue;
            if (val && typeof val === 'object' && 'result' in val) val = val.result;
            result.Findings = typeof val === 'number' ? val : (typeof val === 'string' ? parseInt(val, 10) : 0);
        } catch (e) { console.error(e); }
        // Opatření
        try {
            const measuresRes = await ApiService.api.aggregationMeasure({
                predicates: [
                    {
                        field: 'id',
                        op: QueryOperator.Wrap,
                        comparand: [
                            { field: 'implementerUserId', op: QueryOperator.Eq, comparand: tenantUserId, junction: JunctionOperator.Or },
                            { field: 'measureImplementationUsers.tenantUserId', op: QueryOperator.Eq, comparand: tenantUserId, junction: JunctionOperator.Or },
                        ],
                    },
                    { field: 'implementUntil', op: QueryOperator.Gte, comparand: todayIso },
                    { field: 'implementationState', op: QueryOperator.In, comparand: [ImplementationStateEnum.New, ImplementationStateEnum.InProgress] },
                ],
                aggregation: AggregationEnum.Count,
                aggregationProperty: 'id',
            });
            let val = measuresRes?.data?.returnValue;
            if (val && typeof val === 'object' && 'result' in val) val = val.result;
            result.Measures = typeof val === 'number' ? val : (typeof val === 'string' ? parseInt(val, 10) : 0);
        } catch (e) { console.error(e); }
        // Verifikace
        try {
            const verifRes = await ApiService.api.aggregationMeasure({
                predicates: [
                    {
                        field: 'id',
                        op: QueryOperator.Wrap,
                        comparand: [
                            { field: 'verificationUserId', op: QueryOperator.Eq, comparand: tenantUserId, junction: JunctionOperator.Or },
                            { field: 'measureVerificationUsers.tenantUserId', op: QueryOperator.Eq, comparand: tenantUserId, junction: JunctionOperator.Or },
                        ],
                    },
                    { field: 'implementationState', op: QueryOperator.In, comparand: [ImplementationStateEnum.WaitingForVerification] },
                ],
                aggregation: AggregationEnum.Count,
                aggregationProperty: 'id',
            });
            let val = verifRes?.data?.returnValue;
            if (val && typeof val === 'object' && 'result' in val) val = val.result;
            result.Verification = typeof val === 'number' ? val : (typeof val === 'string' ? parseInt(val, 10) : 0);
        } catch (e) { console.error(e); }
        // Review
        try {
            const reviewRes = await ApiService.api.aggregationPlanItem({
                predicates: [
                    { field: 'requestReviewById', op: QueryOperator.Eq, comparand: tenantUserId },
                    { field: 'planItemState', op: QueryOperator.Eq, comparand: PlanItemStateEnum.WaitingForApproval },
                ],
                aggregation: AggregationEnum.Count,
                aggregationProperty: 'id',
            });
            let val = reviewRes?.data?.returnValue;
            if (val && typeof val === 'object' && 'result' in val) val = val.result;
            result.Review = typeof val === 'number' ? val : (typeof val === 'string' ? parseInt(val, 10) : 0);
        } catch (e) { console.error(e); }
        return result;
    }

    agendaTypeText(type: string): string {
        const map: Record<string, string> = {
            Plan: this.$t('agendaTransfer.plan').toString(),
            Findings: this.$t('agendaTransfer.findings').toString(),
            Measures: this.$t('agendaTransfer.measures').toString(),
            Verification: this.$t('agendaTransfer.verification').toString(),
            Review: this.$t('agendaTransfer.review').toString(),
        };
        return map[type] || type;
    }

    confirmAgendaDeactivation() {
        this.agendaWarningDialog = false;
        this.agendaDeactivationPending = true;
        this.save(); // znovu zavolá save, tentokrát už přeskočí kontrolu
    }

    cancelAgendaDeactivation() {
        this.agendaWarningDialog = false;
        this.agendaDeactivationPending = false;
        // Neprovádí uložení
    }
}
